import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { getPerfil, obtenerPermisosPerfil, logout as cerrarSesion } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import LoaderOverlay from '../components/loader/loader';

const UsuarioContext = createContext(null);

export const UsuarioProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [perfiles, setPerfiles] = useState([]);
  const [perfilActivo, setPerfilActivo] = useState(null)
  const [permisos, setPermisos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const navigate = useNavigate();

  // Obtener información base del usuaario y sus perfiles
  useEffect(() => {
    getPerfil()
      .then((data) => {
        const perfiles = data.perfiles;

        if (!perfiles || perfiles.length === 0) {
          throw new Error('Sin perfiles');
        }
        
        // perfil por defecto
        const perfil = perfiles[0].id_perfil;
        //  || localStorage.getItem('perfilActivo');

        localStorage.setItem('perfilActivo', perfil);
        
        setPerfiles(perfiles);
        setPerfilActivo(perfil);
        setUsuario(data.usuario);
      })
      .catch(() => {
        setUsuario(null);
        setPerfiles([]);
        setPerfilActivo(null);
      })
      .finally(() => setCargando(false)); 
  }, []);

  // Obtener permisos del perfil activo
  useEffect(() => {
    if (!perfilActivo) return;
    
    setCargando(true);

    obtenerPermisosPerfil(perfilActivo)
      .then((data) => {
        setPermisos(data.permisos || []);
      })
      .catch(() => {
        setPermisos([]);
      })
      .finally(() => setCargando(false));
  }, [perfilActivo]);

  // Mapa de permisos
  const permisosMap = useMemo(() => {
    const map = {};

    permisos.forEach(p => {
      if (!map[p.modulo]) {
        map[p.modulo] = new Set();
      }

      map[p.modulo].add(p.permiso);
    });

    return map;
  }, [permisos]);

  const tienePermiso = useCallback((modulo, permiso) => {
    return permisosMap[modulo]?.has(permiso) || false;
  }, [permisosMap]);

  const cambiarPerfil = useCallback((id_perfil) => {
    localStorage.setItem('perfilActivo', id_perfil);
    setPerfilActivo(id_perfil);
  }, []);

  const logout = useCallback(async () => {
    try {
      await cerrarSesion();

      setUsuario(null);
      setPerfilActivo(null);
      setPermisos([]);
      localStorage.removeItem('perfilActivo');

      navigate('/auth/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  }, [navigate]);

  const cambiarContrasena = useCallback(() => {
    navigate('/app/perfil/cambiar-contrasena');
  }, [navigate]);
  
  const value = useMemo(() => ({
    usuario,
    perfiles, perfilActivo, cambiarPerfil,
    permisos, tienePermiso,
    logout,
    cargando,
    cambiarContrasena
  }), [
    usuario,
    perfiles, perfilActivo, cambiarPerfil,
    permisos, tienePermiso,
    logout,
    cargando,
    cambiarContrasena
  ]);

  return (
    <UsuarioContext.Provider value={value}>
      {cargando && <LoaderOverlay />}
      {!cargando && children}
    </UsuarioContext.Provider>
  );
};

export const useUsuario = () => useContext(UsuarioContext);
